import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  IconButton,
  LinearProgress,
  MenuItem,
  Paper,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import PlayArrowIcon from '@mui/icons-material/PlayArrow'
import RefreshIcon from '@mui/icons-material/Refresh'
import ReplayIcon from '@mui/icons-material/Replay'
import VisibilityIcon from '@mui/icons-material/Visibility'
import PageHeader from '../../Shared/PageHeader'
import DataTable from '../../Shared/DataTable'
import ConfirmDialog from '../../Shared/ConfirmDialog'
import { useFeedback } from '../../Shared/FeedbackSnackbar'
import { mensagemDeErro } from '../../../services/apiClient'
import {
  ENTIDADES,
  getConsistency,
  getSyncFailures,
  getSyncStatus,
  retrySyncFailure,
  rotuloDaEntidade,
  runSync,
} from '../../../services/syncServices'
import { listarConexoesSalvas } from '../../../services/migrationServices'
import { formatDateTime, formatNumber } from '../../../utils/format'
import { PERMISSOES } from '../../../const/permissoes'
import { usePode } from '../../../permissions'

const dash = value => (value === null || value === undefined || value === '' ? '—' : value)

const CORES_DO_VEREDITO = {
  OK: 'success',
  DIVERGENTE: 'warning',
  NAO_MIGRADO: 'error',
  NAO_APLICAVEL: 'default',
}

const ROTULOS_DO_VEREDITO = {
  OK: 'OK',
  DIVERGENTE: 'Divergente',
  NAO_MIGRADO: 'Não migrado',
  NAO_APLICAVEL: 'Não aplicável',
}

/**
 * O 403 aqui só pode vir de uma coisa — falta `sincronizacao.visualizar` no
 * SSO. Nomear a permissão poupa o administrador de adivinhar qual conceder.
 */
const SEM_ACESSO_OPERACIONAL =
  'Esta aba exige a permissão sincronizacao.visualizar no SSO. ' +
  'A aba Consistência continua disponível.'

const ehProibido = error => error?.response?.status === 403

const truncate = (text, size = 70) => (text.length > size ? `${text.slice(0, size)}...` : text)

const Sync = () => {
  const { notify, FeedbackSnackbar } = useFeedback()
  const navigate = useNavigate()
  const podeExecutar = usePode(PERMISSOES.SINCRONIZACAO_EXECUTAR)

  const [tab, setTab] = useState(0)
  const [detalhe, setDetalhe] = useState(null)

  // --- Origem: a conexão salva de onde a sincronização lê. Vive na tela de
  // Migração, mas é aqui que ela é USADA — então é aqui que precisa ficar visível
  // se está cadastrada ou não. Sem isto, "não tem origem definida" não dizia onde
  // resolver.
  const [conexoesOrigem, setConexoesOrigem] = useState([])
  const [podeSalvarOrigem, setPodeSalvarOrigem] = useState(true)

  const carregarOrigem = useCallback(async () => {
    try {
      const { items, podeSalvar } = await listarConexoesSalvas()
      setConexoesOrigem(items)
      setPodeSalvarOrigem(podeSalvar)
    } catch {
      // Painel informativo: uma falha aqui não deve poluir a tela com erro.
    }
  }, [])

  // --------------------------------------------------------------- Estado ---
  const [estados, setEstados] = useState([])
  const [loadingEstado, setLoadingEstado] = useState(false)
  const [bloqueioDoEstado, setBloqueioDoEstado] = useState('')
  const [confirmacao, setConfirmacao] = useState(null)
  // Uma rodada de sincronização em curso — roda no servidor, em segundo plano, e
  // a tela acompanha por polling do /sync/status. `progressoSync` conta as
  // entidades já processadas nesta rodada.
  const [sincronizando, setSincronizando] = useState(false)
  const [progressoSync, setProgressoSync] = useState({ feitas: 0, total: 0 })
  const baselineRef = useRef({})
  const viuRodandoRef = useRef(false)
  const pollSyncRef = useRef(null)

  const carregarEstado = useCallback(async () => {
    setLoadingEstado(true)
    try {
      const response = await getSyncStatus()
      setEstados(response.data.result)
      setBloqueioDoEstado('')
    } catch (error) {
      setEstados([])
      if (ehProibido(error)) {
        setBloqueioDoEstado(SEM_ACESSO_OPERACIONAL)
      } else {
        setBloqueioDoEstado('')
        notify(mensagemDeErro(error, 'Não foi possível carregar o estado da sincronização.'), 'error')
      }
    } finally {
      setLoadingEstado(false)
    }
  }, [notify])

  // ---------------------------------------------------------------- Falhas ---
  const [falhas, setFalhas] = useState([])
  const [totalDeFalhas, setTotalDeFalhas] = useState(0)
  const [loadingFalhas, setLoadingFalhas] = useState(false)
  const [bloqueioDasFalhas, setBloqueioDasFalhas] = useState('')
  const [entidadeDaFalha, setEntidadeDaFalha] = useState('')
  const [incluirResolvidas, setIncluirResolvidas] = useState(false)
  const [paginaDeFalhas, setPaginaDeFalhas] = useState(0)
  const [porPagina, setPorPagina] = useState(10)
  const [reprocessando, setReprocessando] = useState('')

  const carregarFalhas = useCallback(async () => {
    setLoadingFalhas(true)
    try {
      const response = await getSyncFailures({
        entidade: entidadeDaFalha,
        incluirResolvidas,
        limit: porPagina,
        offset: paginaDeFalhas * porPagina,
      })
      setFalhas(response.data.result)
      setTotalDeFalhas(response.data.total)
      setBloqueioDasFalhas('')
    } catch (error) {
      setFalhas([])
      setTotalDeFalhas(0)
      if (ehProibido(error)) {
        setBloqueioDasFalhas(SEM_ACESSO_OPERACIONAL)
      } else {
        setBloqueioDasFalhas('')
        notify(mensagemDeErro(error, 'Não foi possível carregar as falhas de sincronização.'), 'error')
      }
    } finally {
      setLoadingFalhas(false)
    }
  }, [entidadeDaFalha, incluirResolvidas, porPagina, paginaDeFalhas, notify])

  // ---------------------------------------------------------- Consistência ---
  const [relatorio, setRelatorio] = useState(null)
  const [loadingRelatorio, setLoadingRelatorio] = useState(false)

  const carregarConsistencia = useCallback(async () => {
    setLoadingRelatorio(true)
    try {
      const response = await getConsistency()
      setRelatorio(response.data)
    } catch (error) {
      setRelatorio(null)
      notify(mensagemDeErro(error, 'Não foi possível gerar o relatório de consistência.'), 'error')
    } finally {
      setLoadingRelatorio(false)
    }
  }, [notify])

  // Cada aba busca só o que mostra: o relatório de consistência varre origem e
  // destino, e carregá-lo junto com o resto tornaria a abertura da tela lenta.
  useEffect(() => {
    if (tab === 0) carregarEstado()
    if (tab === 1) carregarFalhas()
    if (tab === 2 && !relatorio) carregarConsistencia()
  }, [tab, carregarEstado, carregarFalhas, carregarConsistencia, relatorio])

  // A origem independe da aba: é o que diz se a sincronização tem de onde ler.
  useEffect(() => {
    carregarOrigem()
  }, [carregarOrigem])

  const pararPollSync = useCallback(() => {
    if (pollSyncRef.current) {
      clearInterval(pollSyncRef.current)
      pollSyncRef.current = null
    }
  }, [])

  // Para o polling ao desmontar: a tela fechada não deve continuar batendo na API.
  useEffect(() => () => pararPollSync(), [pararPollSync])

  /**
   * Acompanha a rodada em segundo plano pelo /sync/status.
   *
   * Progresso é medido em ENTIDADES: uma entidade está "feita nesta rodada"
   * quando não está mais em execução E a sua última execução avançou em relação
   * ao instante do disparo (baseline). A rodada termina quando nada mais está em
   * execução — depois de já ter visto algo rodar, ou após algumas leituras sem
   * ninguém rodando (rodada instantânea, ou sem nada a trazer).
   */
  const acompanharSync = useCallback(
    entidadesDaRodada => {
      pararPollSync()
      viuRodandoRef.current = false
      let semRodar = 0
      pollSyncRef.current = setInterval(async () => {
        try {
          const items = (await getSyncStatus()).data.result
          setEstados(items)
          const daRodada = items.filter(e => entidadesDaRodada.includes(e.entidade))
          const rodando = daRodada.some(e => e.emExecucao)
          const feitas = daRodada.filter(
            e => !e.emExecucao && e.ultimaExecucao !== baselineRef.current[e.entidade]
          ).length
          setProgressoSync({ feitas, total: daRodada.length })

          if (rodando) {
            viuRodandoRef.current = true
            semRodar = 0
            return
          }
          semRodar += 1
          if (!viuRodandoRef.current && semRodar < 3) return

          pararPollSync()
          setSincronizando(false)
          const todasComErro = daRodada.length > 0 && daRodada.every(e => e.ultimoErro)
          if (todasComErro) {
            notify(daRodada[0].ultimoErro || 'A sincronização falhou.', 'error')
          } else {
            notify('Sincronização concluída.', 'success')
          }
          carregarFalhas()
        } catch (error) {
          pararPollSync()
          setSincronizando(false)
          notify(mensagemDeErro(error, 'Não foi possível acompanhar a sincronização.'), 'error')
        }
      }, 2000)
    },
    [pararPollSync, notify, carregarFalhas]
  )

  const executar = async entidade => {
    const entidadesDaRodada = entidade ? [entidade] : ENTIDADES.map(e => e.valor)
    // Baseline da última execução por entidade: é contra ela que o polling
    // decide o que avançou nesta rodada.
    const baseline = {}
    estados.forEach(e => {
      baseline[e.entidade] = e.ultimaExecucao
    })
    baselineRef.current = baseline
    setConfirmacao(null)
    setSincronizando(true)
    setProgressoSync({ feitas: 0, total: entidadesDaRodada.length })
    try {
      await runSync(entidade ? { entidade } : {})
      notify('Sincronização iniciada — rodando em segundo plano.', 'info')
      acompanharSync(entidadesDaRodada)
    } catch (error) {
      setSincronizando(false)
      notify(mensagemDeErro(error, 'Não foi possível iniciar a sincronização.'), 'error')
      setConfirmacao(null)
    }
  }

  const reprocessar = async falha => {
    setReprocessando(falha.id)
    try {
      const response = await retrySyncFailure(falha.id)
      const status = response.data.status
      if (status === 'RESOLVIDA') {
        notify('Falha reprocessada com sucesso.', 'success')
      } else if (status === 'NAO_ENCONTRADA_NA_ORIGEM') {
        notify('O registro não existe mais na origem; a falha continua visível.', 'warning')
      } else {
        notify(response.data.erro || 'O reprocessamento falhou novamente.', 'error')
      }
      await carregarFalhas()
    } catch (error) {
      notify(mensagemDeErro(error, 'Não foi possível reprocessar a falha.'), 'error')
    } finally {
      setReprocessando('')
    }
  }

  const colunasDoEstado = [
    { id: 'entidade', label: 'Entidade', element: row => rotuloDaEntidade(row.entidade) },
    {
      id: 'situacao',
      label: 'Situação',
      element: row =>
        row.emExecucao ? (
          <Chip size="small" color="info" label="Em execução" />
        ) : row.ultimoErro ? (
          <Chip size="small" color="error" label="Com erro" />
        ) : (
          <Chip size="small" color="success" label="Ociosa" />
        ),
    },
    {
      id: 'marca',
      label: "Marca d'água",
      element: row => (row.ultimaMudancaNaOrigem ? formatDateTime(row.ultimaMudancaNaOrigem) : '—'),
    },
    {
      id: 'ultimaExecucao',
      label: 'Última execução',
      element: row => (row.ultimaExecucao ? formatDateTime(row.ultimaExecucao) : '—'),
    },
    {
      id: 'ultimoSucesso',
      label: 'Último sucesso',
      element: row => (row.ultimoSucesso ? formatDateTime(row.ultimoSucesso) : '—'),
    },
    {
      id: 'registros',
      label: 'Registros',
      align: 'right',
      element: row => formatNumber(row.registrosSincronizados),
    },
    {
      id: 'ultimoErro',
      label: 'Último erro',
      element: row =>
        row.ultimoErro ? (
          <Tooltip title={row.ultimoErro}>
            <Box component="span">{truncate(row.ultimoErro, 40)}</Box>
          </Tooltip>
        ) : (
          '—'
        ),
    },
    ...(podeExecutar
      ? [
          {
            id: 'acoes',
            label: 'Ações',
            align: 'right',
            element: row => (
              <Tooltip title="Sincronizar apenas esta entidade">
                <span>
                  <IconButton
                    size="small"
                    disabled={sincronizando || row.emExecucao}
                    onClick={() => setConfirmacao({ entidade: row.entidade })}
                  >
                    <PlayArrowIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            ),
          },
        ]
      : []),
  ]

  const colunasDasFalhas = [
    { id: 'entidade', label: 'Entidade', element: row => rotuloDaEntidade(row.entidade) },
    { id: 'sourceId', label: 'Id na origem', element: row => dash(row.sourceId) },
    { id: 'ocorridaEm', label: 'Ocorrida em', element: row => formatDateTime(row.ocorridaEm) },
    { id: 'tentativas', label: 'Tentativas', align: 'right', element: row => row.tentativas },
    {
      id: 'erro',
      label: 'Erro',
      element: row => (
        <Tooltip title={row.erro}>
          <Box component="span">{truncate(row.erro)}</Box>
        </Tooltip>
      ),
    },
    {
      id: 'situacao',
      label: 'Situação',
      element: row =>
        row.resolvidaEm ? (
          <Chip size="small" color="success" label={`Resolvida em ${formatDateTime(row.resolvidaEm)}`} />
        ) : (
          <Chip size="small" color="warning" label="Pendente" />
        ),
    },
    {
      id: 'acoes',
      label: 'Ações',
      align: 'right',
      element: row => (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}>
          <Tooltip title="Ver payload de origem">
            <span>
              <IconButton
                size="small"
                disabled={!row.payload}
                onClick={() => setDetalhe({ title: 'Payload de origem', value: row.payload })}
              >
                <VisibilityIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          {podeExecutar && (
            <Tooltip title="Reprocessar">
              <span>
                <IconButton
                  size="small"
                  disabled={Boolean(row.resolvidaEm) || reprocessando === row.id}
                  onClick={() => reprocessar(row)}
                >
                  <ReplayIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          )}
        </Box>
      ),
    },
  ]

  const colunasDaConsistencia = [
    { id: 'entidade', label: 'Entidade', element: row => rotuloDaEntidade(row.entidade) },
    {
      id: 'veredito',
      label: 'Veredito',
      element: row => (
        <Chip
          size="small"
          color={CORES_DO_VEREDITO[row.veredito] || 'default'}
          label={ROTULOS_DO_VEREDITO[row.veredito] || row.veredito}
        />
      ),
    },
    { id: 'origem', label: 'Origem', align: 'right', element: row => formatNumber(row.totalOrigem) },
    {
      id: 'destino',
      label: 'Destino',
      align: 'right',
      element: row => formatNumber(row.totalDestino),
    },
    {
      id: 'diferenca',
      label: 'Diferença',
      align: 'right',
      element: row => formatNumber(row.diferenca),
    },
    { id: 'amostrados', label: 'Amostrados', align: 'right', element: row => formatNumber(row.amostrados) },
    {
      id: 'divergencias',
      label: 'Divergências de campo',
      element: row =>
        row.divergenciasDeCampo.length ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box component="span">{row.divergenciasDeCampo.length}</Box>
            <IconButton
              size="small"
              onClick={() =>
                setDetalhe({
                  title: `Divergências — ${rotuloDaEntidade(row.entidade)}`,
                  value: row.divergenciasDeCampo,
                })
              }
            >
              <VisibilityIcon fontSize="small" />
            </IconButton>
          </Box>
        ) : (
          '—'
        ),
    },
    { id: 'observacao', label: 'Observação', element: row => dash(row.observacao) },
  ]

  return (
    <Box>
      <PageHeader
        title="Sincronização"
        subtitle="Carga incremental do legado e conferência de consistência"
        actions={
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={() => {
                if (tab === 0) carregarEstado()
                if (tab === 1) carregarFalhas()
                if (tab === 2) carregarConsistencia()
              }}
            >
              Atualizar
            </Button>
            {podeExecutar && tab === 0 && (
              <Button
                variant="contained"
                startIcon={<PlayArrowIcon />}
                disabled={sincronizando || Boolean(bloqueioDoEstado)}
                onClick={() => setConfirmacao({ entidade: '' })}
              >
                Sincronizar agora
              </Button>
            )}
          </Box>
        }
      />

      {/*
        Origem da sincronização — cadastrada na tela de Migração, mas mostrada
        aqui, onde é usada. Sem este painel, "não tem origem definida" não dizia
        onde resolver, e não havia como saber se já havia uma conexão salva.
      */}
      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Typography variant="subtitle1" fontWeight={600} gutterBottom>
          Origem da sincronização
        </Typography>

        {conexoesOrigem.length > 0 ? (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              A sincronização reconecta sozinha usando a conexão salva mais recente. A senha fica
              cifrada — nunca é devolvida.
            </Typography>
            {conexoesOrigem.map(c => (
              <Box key={c.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                <Chip
                  size="small"
                  color={c.temSenha ? 'success' : 'default'}
                  label={c.temSenha ? 'pronta' : 'sem senha'}
                />
                <Typography variant="body2">
                  <strong>{c.nome}</strong> — {c.host}/{c.database || '—'}
                  {c.escopo
                    ? ` · 1 empresa, ${c.escopo.subCompanyIds.length} filial(is)`
                    : ' · banco inteiro'}
                </Typography>
              </Box>
            ))}
            <Button size="small" sx={{ mt: 1 }} onClick={() => navigate('/Migration')}>
              Gerenciar na tela de Migração
            </Button>
          </>
        ) : (
          <Alert
            severity="warning"
            action={
              <Button color="inherit" size="small" onClick={() => navigate('/Migration')}>
                Cadastrar
              </Button>
            }
          >
            <AlertTitle>Nenhuma origem cadastrada</AlertTitle>
            A sincronização não tem de onde ler. Cadastre na tela de <strong>Migração</strong>:
            preencha o servidor de origem, escolha a empresa e as filiais e marque{' '}
            <strong>“Salvar esta conexão para sincronização”</strong>.
            {!podeSalvarOrigem &&
              ' (Atenção: a criptografia de senha ainda não está configurada no servidor — o' +
                ' botão de salvar não aparece até isso ser resolvido.)'}
          </Alert>
        )}
      </Paper>

      <Tabs value={tab} onChange={(event, novo) => setTab(novo)} sx={{ mb: 3 }}>
        <Tab label="Estado" />
        <Tab label="Falhas" />
        <Tab label="Consistência" />
      </Tabs>

      {tab === 0 && (
        <Box>
          <Alert severity="info" sx={{ mb: 3 }}>
            A sincronização traz do legado apenas o que mudou desde a marca d&apos;água de cada
            entidade e aplica por MERGE idempotente — reexecutar não duplica. Um registro que falha
            vai para a aba Falhas e a rodada continua. Roda em <strong>segundo plano</strong>: pode
            sair desta tela que ela continua.
          </Alert>

          {sincronizando && (
            <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                <CircularProgress size={16} />
                <Typography variant="body2" fontWeight={600}>
                  Sincronizando em segundo plano
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {progressoSync.feitas} de {progressoSync.total} entidade(s)
                  {progressoSync.total
                    ? ` · ${Math.round((progressoSync.feitas / progressoSync.total) * 100)}%`
                    : ''}
                </Typography>
              </Stack>
              <LinearProgress
                sx={{ height: 8, borderRadius: 4 }}
                variant={progressoSync.total ? 'determinate' : 'indeterminate'}
                value={
                  progressoSync.total
                    ? Math.round((progressoSync.feitas / progressoSync.total) * 100)
                    : 0
                }
              />
            </Paper>
          )}

          {bloqueioDoEstado ? (
            <Alert severity="warning">{bloqueioDoEstado}</Alert>
          ) : (
            <DataTable
              columns={colunasDoEstado}
              rows={estados}
              loading={loadingEstado}
              emptyMessage="Nenhuma entidade sincronizada até agora."
              pagination={false}
              rowKey={row => row.entidade}
            />
          )}
        </Box>
      )}

      {tab === 1 && (
        <Box>
          {bloqueioDasFalhas ? (
            <Alert severity="warning">{bloqueioDasFalhas}</Alert>
          ) : (
            <>
              <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} md={4}>
                    <TextField
                      select
                      fullWidth
                      label="Entidade"
                      value={entidadeDaFalha}
                      onChange={event => {
                        setEntidadeDaFalha(event.target.value)
                        setPaginaDeFalhas(0)
                      }}
                    >
                      <MenuItem value="">Todas</MenuItem>
                      {ENTIDADES.map(item => (
                        <MenuItem key={item.valor} value={item.valor}>
                          {item.rotulo}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={incluirResolvidas}
                          onChange={event => {
                            setIncluirResolvidas(event.target.checked)
                            setPaginaDeFalhas(0)
                          }}
                        />
                      }
                      label="Incluir resolvidas"
                    />
                  </Grid>
                </Grid>
              </Paper>

              <DataTable
                columns={colunasDasFalhas}
                rows={falhas}
                loading={loadingFalhas}
                emptyMessage="Nenhuma falha de sincronização pendente."
                page={paginaDeFalhas}
                rowsPerPage={porPagina}
                total={totalDeFalhas}
                rowKey={row => row.id}
                onPageChange={(event, nova) => setPaginaDeFalhas(nova)}
                onRowsPerPageChange={event => {
                  setPorPagina(parseInt(event.target.value, 10))
                  setPaginaDeFalhas(0)
                }}
              />
            </>
          )}
        </Box>
      )}

      {tab === 2 && (
        <Box>
          <Alert severity="info" sx={{ mb: 3 }}>
            Contagem igual não prova conteúdo igual: além dos totais, o relatório compara uma
            amostra de campos-chave. O escopo é sempre recortado pelas suas filiais no SSO.
          </Alert>

          {relatorio && (
            <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={4}>
                  <Typography variant="body2" color="text.secondary">
                    Veredito geral
                  </Typography>
                  <Chip
                    color={CORES_DO_VEREDITO[relatorio.veredito] || 'default'}
                    label={ROTULOS_DO_VEREDITO[relatorio.veredito] || relatorio.veredito}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography variant="body2" color="text.secondary">
                    Gerado em
                  </Typography>
                  <Typography>{formatDateTime(relatorio.geradoEm)}</Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography variant="body2" color="text.secondary">
                    Filiais no escopo
                  </Typography>
                  <Typography>{relatorio.escopo.length || 'todas'}</Typography>
                </Grid>
              </Grid>
            </Paper>
          )}

          <DataTable
            columns={colunasDaConsistencia}
            rows={relatorio ? relatorio.entidades : []}
            loading={loadingRelatorio}
            emptyMessage="Nenhuma entidade no relatório."
            pagination={false}
            rowKey={row => row.entidade}
          />
        </Box>
      )}

      <ConfirmDialog
        open={Boolean(confirmacao)}
        title="Executar sincronização"
        description={
          confirmacao?.entidade
            ? `Sincronizar "${rotuloDaEntidade(confirmacao.entidade)}" a partir do legado?`
            : 'Sincronizar todas as entidades a partir do legado?'
        }
        confirmLabel="Executar"
        confirmColor="primary"
        loading={sincronizando}
        onConfirm={() => executar(confirmacao?.entidade || '')}
        onClose={() => setConfirmacao(null)}
      />


      <Dialog open={Boolean(detalhe)} onClose={() => setDetalhe(null)} fullWidth maxWidth="md">
        <DialogTitle>{detalhe && detalhe.title}</DialogTitle>
        <DialogContent dividers>
          <Box
            component="pre"
            sx={{
              m: 0,
              maxHeight: '60vh',
              overflow: 'auto',
              fontSize: 13,
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
            }}
          >
            {detalhe && JSON.stringify(detalhe.value, null, 2)}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button color="inherit" onClick={() => setDetalhe(null)}>
            Fechar
          </Button>
        </DialogActions>
      </Dialog>

      <FeedbackSnackbar />
    </Box>
  )
}

export default Sync
